// @ts-strict-ignore
import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { createSelector, Store } from '@ngrx/store';
import { combineLatest, EMPTY, Observable, of } from 'rxjs';
import { first, map, switchMap } from 'rxjs/operators';
import type { ValueOf } from 'type-fest';

import { ConflictComponent, HealingComponent, Prices, Vehicle, VehicleComponent } from 'src/app/api/owcc/models';
import { AbTestingService } from 'src/app/core/services/ab-testing/ab-testing.service';
import { ActivatedRouteState } from 'src/app/core/services/activated-route/activated-route-state.interface';
import { ConfigurationService } from 'src/app/features/configuration/configuration.service';
import { AppRoutePath } from 'src/app/routing/app-route-path.enum';
import { FeasibilityResultAlternative } from 'src/app/shared/feasibility-conflict-modal/feasibility-result-alternative.interface';
import { FeasibilityResult } from 'src/app/shared/feasibility-conflict-modal/feasibility-result.interface';
import { NavigationGroup } from 'src/app/shared/navigation/navigation-group.interface';
import {
  selectConfigurationVehicle,
  selectCurrentNavigationElementGroup,
  selectSelectedComponents
} from 'src/app/store/common/common.selectors';
import { selectIsMotorizationOrPreconfigOrVehiclelength } from 'src/app/store/router/router.selectors';
import {
  selectCompanyId,
  selectOnlineCode,
  selectOnlineCodeVcsId,
  selectSubprocess
} from 'src/app/store/runtime-variables/runtime-variables.selectors';
import { filterTruthy } from 'src/app/utils/functions/rx-filters';
import { EnrichedComponentCategory } from 'src/app/utils/models/enriched-component-category.type';
import { ExtendedVehicleComponent } from 'src/app/utils/models/extended-vehicle-component.interface';
import { Subprocess } from 'src/app/utils/models/subprocess.enum';
import { VehicleComponentCollection } from 'src/app/utils/models/vehicle-component-collection.interface';
import { OWCC_APP_NAME, OWCC_APP_VERSION } from 'src/build-env';

import { OWC_LOCAL_STORAGE_KEY } from '../../config/web-storage.config';
import { LocalStorageService } from '../../services/storage/local-storage/local-storage.service';

import { TrackingAction } from './models/tracking-action.enum';
import { TrackingCategory } from './models/tracking-category.enum';
import { TrackingEventType } from './models/tracking-event-type.enum';
import { TrackingItemType } from './models/tracking-item-type.enum';
import { TrackingLabel } from './models/tracking-label.enum';
import {
  TrackingApplication,
  TrackingEventDetail,
  TrackingInput,
  TrackingItem,
  TrackingObject,
  TrackingPage,
  TrackingSearch,
  TrackingVehicle
} from './models/tracking-object.interface';
import { TrackingPagename } from './models/tracking-pagename.enum';
import { TrackingPlaceholder } from './models/tracking-placeholder.enum';

const selectTrackingApplication = createSelector(selectSubprocess, subprocess => ({
  id: OWCC_APP_NAME,
  version: OWCC_APP_VERSION,
  configId: subprocess || Subprocess.CCci
}));

@Injectable({
  providedIn: 'root'
})
export class TrackingDataService {
  public constructor(
    private readonly store: Store,
    private readonly localStorageService: LocalStorageService,
    private readonly abTestingSevice: AbTestingService,
    @Inject(DOCUMENT) private readonly document: Document,
    private readonly configurationService: ConfigurationService
  ) {}

  public static getTrackingInputForConflicts(
    label: TrackingLabel,
    activatedRoute: ActivatedRouteState,
    feasibilityResult: FeasibilityResult,
    components: VehicleComponentCollection,
    search: TrackingSearch,
    componentLabels: string[]
  ): TrackingInput {
    const { price, added, removed, updated } = feasibilityResult.alternatives?.[0] || {};
    return {
      event: TrackingEventType.Click,
      routeState: activatedRoute.isSummary ? TrackingPagename.Summary : activatedRoute.configurationStep,
      alternatives: feasibilityResult.alternatives,
      conflictItem: feasibilityResult.conflictItem,
      clickData: {
        category: TrackingCategory.Feature,
        label: `${TrackingDataService.getLabelItemChange(label, feasibilityResult.conflictItem, componentLabels)}|${[
          price?.price ?? 0,
          [added?.length ?? 0, TrackingDataService.countIncludedComponents(added)].join('.'),
          [removed?.length ?? 0, TrackingDataService.countIncludedComponents(removed)].join('.'),
          [updated?.length ?? 0, TrackingDataService.countIncludedComponents(updated)].join('.')
        ].join('|')}`,
        action: TrackingAction.FeasibilityConflict,
        search
      }
    };
  }

  //alternatives is a part of feasibilityResult array (added/removed/updated)
  public static countIncludedComponents(alternatives: (ExtendedVehicleComponent | HealingComponent)[]): number {
    return (
      alternatives?.reduce(
        (totalLength, alternative) => totalLength + (alternative?.includedComponents?.length || 0),
        0
      ) || 0
    );
  }

  public static getLabelItemChange(
    label: TrackingLabel,
    conflictItem: ConflictComponent,
    componentLabels: string[]
  ): string {
    const trackingComponentLabels = componentLabels?.join(':')?.toLowerCase() ?? '';
    const trackingLabel = label + (conflictItem?.state ? `.${conflictItem?.state?.toLowerCase()}` : '');
    return [trackingLabel, trackingComponentLabels, conflictItem.id ?? '', conflictItem?.price?.price ?? 0]
      .filter(n => n !== '')
      .join('|');
  }

  private static getPrice(price: Prices): string | number {
    return price?.price ?? TrackingPlaceholder.PriceUndefined;
  }

  private static getItemName(name: string): string | TrackingPlaceholder {
    return name ? name.replaceAll(/[\t\n"']/g, '') : TrackingPlaceholder.ItemNameUndefined;
  }

  public getSelectables(selectedComponents: VehicleComponent[]): TrackingItem[] {
    const items: TrackingItem[] = [];
    if (selectedComponents) {
      for (const component of selectedComponents) {
        items.push({
          id: component.id,
          category: component?.type?.toLowerCase() ?? '',
          name: component.name,
          value: TrackingDataService.getPrice(component?.price)
        });
      }
    }
    return items;
  }

  public getTrackingData(trackingInput: TrackingInput): Observable<TrackingObject | undefined> {
    return this.abTestingSevice.getAbTestingTracking$().pipe(
      switchMap(targetTracking => {
        if (targetTracking.abTest) {
          trackingInput.target = targetTracking;
        }
        const salesExpertOutletId = this.getSalesExpertOutletIdFromLocalStorage();
        if (salesExpertOutletId) {
          trackingInput.retailerId = salesExpertOutletId;
          if (trackingInput.event === TrackingEventType.Click) {
            trackingInput.clickData.retailerId = salesExpertOutletId;
          }
        }
        return this.store.select(selectIsMotorizationOrPreconfigOrVehiclelength).pipe(
          first(),
          switchMap(isMotorizationOrBeforePage => {
            const routeStateIsMotorizationOrBeforePage = [
              TrackingPagename.Motorization,
              TrackingPagename.VehicleLength,
              TrackingPagename.Preconfig
            ].includes(trackingInput.routeState as TrackingPagename);
            if (isMotorizationOrBeforePage && routeStateIsMotorizationOrBeforePage) {
              return this.getMotoData(trackingInput);
            } else if (this.configurationService && !trackingInput.errorData) {
              return this.getConfigData(trackingInput);
            } else if (trackingInput.errorData || (!isMotorizationOrBeforePage && !this.configurationService)) {
              return this.getErrorData(trackingInput);
            }
            return EMPTY;
          })
        );
      })
    );
  }

  public getLinkData(trackingInput: TrackingInput): Observable<TrackingEventDetail | null> {
    if (!trackingInput.clickData) {
      return of(null);
    }

    return this.getTrackingVehicleConfiguration(trackingInput).pipe(
      map(vehicle => {
        const typeList = ['PC-PWU', 'PC-PWV', 'PC-PWS', 'PC-PWT'];

        const trackingEventDetail: TrackingEventDetail = {
          label: trackingInput.clickData.label,
          category: trackingInput.clickData.category,
          action: trackingInput.clickData.action,
          vehicle
        };

        if (trackingInput.clickData.retailerId) {
          trackingEventDetail.retailerId = trackingInput.clickData.retailerId;
        }
        if (trackingInput.target) {
          trackingEventDetail.target = trackingInput.target;
        }

        if (trackingInput.conflictItem) {
          trackingEventDetail.items = this.getItems(trackingInput);
          trackingEventDetail.label = typeList.includes(trackingInput.conflictItem?.id)
            ? `${trackingEventDetail.label}:${trackingInput.conflictItem.id}`
            : trackingEventDetail.label;
        }

        if (trackingInput.clickData?.search) {
          trackingEventDetail.search = trackingInput.clickData.search;
        }

        return trackingEventDetail;
      })
    );
  }

  private getSalesExpertOutletIdFromLocalStorage(): string | undefined {
    return this.localStorageService.get<{ claims?: { home_org_alias?: string } }>(OWC_LOCAL_STORAGE_KEY.B2XCORE_AUTH)
      ?.claims?.home_org_alias;
  }

  private extractPrice(obj: Prices): number | TrackingPlaceholder {
    return obj?.price ?? TrackingPlaceholder.PriceUndefined;
  }

  private getTrackingApplication(): Observable<TrackingApplication> {
    return this.store.select(selectTrackingApplication).pipe(first());
  }

  private getTrackingMotorization(trackingInput: TrackingInput): Observable<TrackingPage> {
    return combineLatest([
      this.getTrackingVehicleMotorization(
        (trackingInput.routeState as TrackingPagename) === TrackingPagename.Preconfig
      ),
      this.getTrackingDealerId()
    ]).pipe(
      map(([vehicle, retailerId]) => {
        const trackingPage: TrackingPage = {
          name: trackingInput.routeState,
          type: 'configuration',
          vehicle
        };

        if (trackingInput.retailerId || retailerId) {
          trackingPage.retailerId = trackingInput.retailerId ?? retailerId;
        }
        if (trackingInput.target) {
          trackingPage.target = trackingInput.target;
        }

        return trackingPage;
      }),
      first()
    );
  }

  private getTrackingConfiguration(trackingInput: TrackingInput): Observable<TrackingPage> {
    return combineLatest([
      this.getTrackingVehicleConfiguration(trackingInput),
      this.getTrackingVehicleItems(),
      this.getTrackingDealerId()
    ]).pipe(
      map(([vehicle, items, retailerId]) => {
        const trackingPage: TrackingPage = {
          name: trackingInput.routeState,
          type: 'configuration',
          vehicle
        };

        if (trackingInput.retailerId || retailerId) {
          trackingPage.retailerId = trackingInput.retailerId ?? retailerId;
        }
        if (trackingInput.target) {
          trackingPage.target = trackingInput.target;
        }
        if ((trackingInput.routeState as TrackingPagename) === TrackingPagename.Summary) {
          trackingPage.items = items;
        }
        return trackingPage;
      }),
      first()
    );
  }

  private getModelCode(vehicle: Vehicle): TrackingPlaceholder | string {
    return vehicle?.typeClass ?? TrackingPlaceholder.ModelCodeUndefined;
  }

  private getModelYear(vehicle: Vehicle): TrackingPlaceholder | string {
    return vehicle?.modelYear ?? TrackingPlaceholder.UniversalUndefined;
  }

  private getChangeYear(vehicle: Vehicle): TrackingPlaceholder | string {
    return vehicle?.changeYear ?? TrackingPlaceholder.UniversalUndefined;
  }

  private getTrackingVehicleMotorization(isPreconfig: boolean): Observable<TrackingVehicle | null> {
    // in the case of Preconfigs, there is no selected Vehicle yet, so we return null here
    if (isPreconfig) {
      return of(null);
    }
    return this.store.select(selectConfigurationVehicle).pipe(
      filterTruthy(),
      first(),
      map((vehicle: Vehicle) => ({
        baumusterId: vehicle.baumuster,
        modelCode: this.getModelCode(vehicle),
        modelYear: this.getModelYear(vehicle),
        changeYear: this.getChangeYear(vehicle),
        price: this.extractPrice(vehicle?.priceInformation?.basePrice),
        priceTotal: this.extractPrice(vehicle?.priceInformation?.configurationPrice),
        nst: vehicle?.nst,
        vehicleId: vehicle?.vehicleId,
        brand: vehicle?.brand
      }))
    );
  }

  private getTrackingVehicleConfiguration(trackingInput: TrackingInput): Observable<TrackingVehicle> {
    return combineLatest([
      this.store.select(selectConfigurationVehicle).pipe(filterTruthy()),
      this.getOnlinecode(trackingInput),
      this.getOnlinecodeVcsId(trackingInput)
    ]).pipe(
      map(([vehicle, onlineCode, vcsId]: [Vehicle, string, string]) => ({
        modelCode: this.getModelCode(vehicle),
        modelYear: this.getModelYear(vehicle),
        changeYear: this.getChangeYear(vehicle),
        price: this.extractPrice(vehicle?.priceInformation?.basePrice),
        priceTotal: this.extractPrice(vehicle?.priceInformation?.configurationPrice),
        nst: vehicle?.nst,
        baumusterId: vehicle?.baumuster,
        vehicleId: vehicle?.vehicleId,
        brand: vehicle?.brand,
        onlineCode,
        vcsId
      })),
      first()
    );
  }

  private getTrackingVehicleItems(): Observable<TrackingItem[]> {
    return this.store.select(selectSelectedComponents).pipe(
      filterTruthy(),
      map(components => this.getSelectables(components as VehicleComponent[])),
      first()
    );
  }

  private getTrackingDealerId(): Observable<string> {
    return this.store.select(selectCompanyId).pipe(first());
  }

  /**
   *
   * sets tracking data if the user is on the moto page
   */
  private getMotoData(trackingInput: TrackingInput): Observable<TrackingObject> {
    return combineLatest([
      this.getTrackingApplication(),
      this.getTrackingMotorization(trackingInput),
      this.getLinkData(trackingInput),
      this.getSelectableCategory()
    ]).pipe(
      map(([application, page, eventDetail]) =>
        this.createTrackingObject(trackingInput, application, page, eventDetail)
      ),
      first()
    );
  }

  private getSelectableCategory(): Observable<EnrichedComponentCategory | NavigationGroup> | undefined {
    return this.store.select(selectCurrentNavigationElementGroup).pipe(first());
  }

  /**
   *
   * sets tracking data if the user is on the config page
   */
  private getConfigData(trackingInput: TrackingInput): Observable<TrackingObject> {
    return combineLatest([
      this.getTrackingApplication(),
      this.getTrackingConfiguration(trackingInput),
      this.getLinkData(trackingInput),
      this.getSelectableCategory()
    ]).pipe(
      map(([application, page, eventDetail]) =>
        this.createTrackingObject(trackingInput, application, page, eventDetail)
      ),
      first()
    );
  }

  private createTrackingObject(
    trackingInput: TrackingInput,
    application: TrackingApplication,
    page: TrackingPage,
    eventDetail: TrackingEventDetail
  ): TrackingObject {
    const trackingObject: TrackingObject = {
      event: trackingInput.event,
      application
    };

    if (trackingInput.clickData) {
      trackingObject.eventDetail = eventDetail;
    } else {
      page.name = `car-configurator.${page.name}`;
      trackingObject.page = page;
    }

    return trackingObject;
  }

  private getErrorData(trackingInput: TrackingInput): Observable<TrackingObject> {
    return this.getTrackingApplication().pipe(
      map(application => {
        let trackingObject: TrackingObject;
        if ((trackingInput.routeState as AppRoutePath) === AppRoutePath.ErrorPage) {
          trackingObject = {
            event: TrackingEventType.PageView,
            application,
            page: {
              name: 'error',
              type: 'configuration',
              errorDescription: trackingInput.errorData.errorDescription,
              errorType: trackingInput.errorData.errorType,
              // eslint-disable-next-line unicorn/better-regex
              errorUrl: this.document.URL.split(/__(?:[A-Z]*-)/)[0]
            }
          };
          if (trackingInput.retailerId) {
            trackingObject.page.retailerId = trackingInput.retailerId;
          }
        } else {
          trackingObject = {
            event: TrackingEventType.Click,
            application,
            eventDetail: {
              errorDescription: trackingInput.errorData?.errorDescription,
              errorType: trackingInput.errorData?.errorType,
              label: trackingInput.clickData?.label,
              category: trackingInput.clickData?.category,
              action: trackingInput.clickData?.action
            }
          };
        }
        return trackingObject;
      }),
      first()
    );
  }

  private getOnlinecode(trackingInput: TrackingInput): Observable<string> {
    return (trackingInput.routeState as TrackingPagename) === TrackingPagename.Summary
      ? this.store.select(selectOnlineCode).pipe(filterTruthy())
      : this.store.select(selectOnlineCode);
  }

  private getOnlinecodeVcsId(trackingInput: TrackingInput): Observable<string> {
    return (trackingInput.routeState as TrackingPagename) === TrackingPagename.Summary
      ? this.store.select(selectOnlineCodeVcsId).pipe(filterTruthy())
      : this.store.select(selectOnlineCodeVcsId);
  }

  private getItems(trackingInput: TrackingInput): TrackingItem[] {
    const trackingItems: TrackingItem[] = [];
    const { id, name, type, price, state } = trackingInput?.conflictItem || {};

    const baseTrackingItem: Readonly<TrackingItem> = {
      id,
      name: TrackingDataService.getItemName(name),
      category: type?.toLowerCase(),
      event: state?.toLowerCase(),
      value: TrackingDataService.getPrice(price)
    };

    const addTrackingItem = (itemPartial: Partial<TrackingItem> = {}): void => {
      const item: TrackingItem = { ...baseTrackingItem, ...itemPartial };
      trackingItems.push(item);
    };

    if (trackingInput.alternatives) {
      addTrackingItem({ event: 'conflict' });
      this.trackEachConflictAlternative(trackingInput.alternatives, addTrackingItem);
    } else {
      addTrackingItem();
    }

    return trackingItems;
  }

  private trackEachConflictAlternative(
    alternatives: FeasibilityResultAlternative[],
    callback: (item: TrackingItem) => void
  ): void {
    for (const alternativeKey in TrackingItemType) {
      if (
        TrackingItemType[alternativeKey] &&
        alternatives[0][TrackingItemType[alternativeKey] as keyof FeasibilityResultAlternative]
      ) {
        for (const item of (alternatives[0][
          TrackingItemType[alternativeKey] as keyof FeasibilityResultAlternative
        ] as ValueOf<FeasibilityResultAlternative, TrackingItemType>) || []) {
          callback({
            id: item.id,
            name: TrackingDataService.getItemName((item as ExtendedVehicleComponent).label || ''),
            category: (item as ExtendedVehicleComponent).type?.toLowerCase() || '',
            event: TrackingItemType[alternativeKey as keyof typeof TrackingItemType].toLowerCase(),
            value: TrackingDataService.getPrice(item.price)
          });
        }
      }
    }
  }
}
